import { TextArea } from '@latitude/text-area';
import { FormComponentWrapper } from 'components/FormComponentWrapper';
import React, { useId } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { FormTextAreaProps } from './types';

export const FormTextArea = <T extends FieldValues = FieldValues>({
  name,
  control,
  description,
  label,
  wrapperClassname,
  message,
  optional,
  rules,
  disabled,
  id: passedId,
  onBlur: passedOnBlur,
  ...textAreaProps
}: FormTextAreaProps<T>) => {
  const generatedId = useId();
  // Shared with `FormComponentWrapper`'s label `htmlFor` so the rendered
  // label and textarea are actually associated (a11y + RTL `getByLabelText`).
  const textAreaId = passedId ?? generatedId;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onBlur, onChange, value },
        fieldState: { error },
      }) => (
        <FormComponentWrapper
          name={name}
          id={textAreaId}
          description={description}
          label={label}
          error={error}
          className={wrapperClassname}
          message={message}
          optional={optional}
          disabled={disabled}
        >
          {/* Value needs to be set as an empty string instead of undefined since if it is it will be treated as an uncontrolled input */}
          <TextArea
            id={textAreaId}
            disabled={disabled}
            value={value ?? ''}
            onBlur={(event: React.FocusEvent<HTMLTextAreaElement>) => {
              onBlur();
              passedOnBlur?.(event);
            }}
            onChange={onChange}
            error={!!error}
            rawProps={{
              'aria-invalid': error ? 'true' : undefined,
            }}
            {...textAreaProps}
          />
        </FormComponentWrapper>
      )}
    />
  );
};
